import React, { useState, useEffect, useRef } from 'react'
import Filter from '../../components/Filter'
import {
	Row,
	Col,
	Image,
	FormControl,
	Button,
	Modal
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	faFileDownload,
	faChevronRight,
	faChevronLeft,
	faArrowRight
} from '@fortawesome/free-solid-svg-icons'
import styles from './Applications.module.scss'
import 'react-day-picker/lib/style.css'
import ReactPaginate from 'react-paginate'
import dayjs from 'dayjs'
import moment from 'moment'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import { CSVLink } from 'react-csv'
import axios from 'axios'
import { useLocation } from 'react-router-dom'
import EButton from '../../components/bootstrap-extended/EButton'
import BulkDataService from '../../services/BulkDataService'
import { saveAs } from 'file-saver'

interface DateCSSProps {
	classNames: {
		overlayWrapper: string,
		overlay: string
	},
	children: React.ReactNode
}

const CustomOverlay: React.FC<DateCSSProps> = ({ classNames, children, ...props }) => {
	return (
		<div
			className={classNames.overlayWrapper}
			style={{ marginLeft: -85 }}
			{...props}
		>
			<div className={classNames.overlay}>
				{children}
			</div>
		</div>
	)
}

interface DataProps {
	jobApplied: string
	company: string
	applicantName: string
	status: string
	appliedOn: string
	id: number
	kiosk: string
}

function useQuery() {
	const { search } = useLocation()
	return React.useMemo(() => new URLSearchParams(search), [search])
}

const JobApplications = () => {
	const csvLink = useRef<any | null>(null)
	const query = useQuery()

	const [jobApplications, setJobApplications] = useState<Array<DataProps> | null>([])
	const [pageNo, setPageNo] = useState('')
	const [toPage, setToPage] = useState<number>(0)
	const [isLoading, setIsLoading] = useState(true)

	const [dataExport, setDataExport] = useState<any[]>([])
	const [dataAggregate, setDataAggregate] = useState(0)

	const [startDate, setStartDate] = useState<string>('')
	const [endDate, setEndDate] = useState<string>('')
	const [filterPage, setFilterPage] = useState('')

	// pagination
	const [pageCount, setPageCount] = useState(0)
	const [itemOffset, setItemOffset] = useState(0)
	const itemsPerPage = 10

	const [show, setShow] = useState(false)
	const [employerAction, setEmployerAction] = useState({
		pending: null,
		viewed: null,
		unlocked: null,
		shortlisted: null,
		acceptedRejected: null,
		finalDecision: null
	})
	const { pending, viewed, unlocked, acceptedRejected, finalDecision } = employerAction

	const exportHeader = [
		{ label: 'Job Position', key: 'jobApplied' },
		{ label: 'Company Name', key: 'company' },
		{ label: 'Applicant Name', key: 'applicantName' },
		{ label: 'Status', key: 'status' },
		{ label: 'Applied On', key: 'appliedOn' }
	]

	useEffect(() => {
		const toNum = () => {
			if (pageNo === '') {
				setItemOffset(0)
				setToPage(0)
			} else {
				const temp = parseInt(pageNo)
				const newOffset = (temp * 10 - 10)
				setItemOffset(newOffset)
				setToPage(temp - 1)
			}
		}
		toNum()
	}, [pageNo])

	const page = query.get('page')
	const startDateQuery = query.get('startDate')
	const endDateQuery = query.get('endDate')

	const filters = {
		page: (page !== null && parseInt(page)) ? page : 1,
		startDate: (startDateQuery !== null && startDateQuery) ? startDateQuery : '',
		endDate: (endDateQuery !== null && endDateQuery) ? endDateQuery : '',
	}

	const handlePageClick = (event: { selected: number }) => {
		window.location.href = getPaginationLink((event.selected + 1).toString())
	}

	const getPaginationLink = (page: string): string => {
		const startDate: string = filters.startDate as string
		const endDate: string = filters.endDate as string
		return `/applications?page=${page}&startDate=${startDate}&endDate=${endDate}`
	}

	const handleDateChange = (startDateSelected: string, endDateSelected: string): void => {
		window.location.href = `/applications?page=1&startDate=${startDateSelected}&endDate=${endDateSelected}`
	}

	useEffect(() => {
		const getJobs = async () => {
			try {
				const res = await axios.get(process.env.REACT_APP_ROUTE_GET_APPLICATION as string + `?page=${filters.page}&startDate=${filters.startDate}&endDate=${filters.endDate}`)
				setJobApplications(res.data.applications)
				setIsLoading(false)
				setPageCount(Math.ceil(res.data.applicationAggregate / itemsPerPage))
				setStartDate(filters.startDate)
				setEndDate(filters.endDate)
				setPageNo(filters.page.toString())
				setDataAggregate(res.data.applicationAggregate)
			} catch (err) {
				console.log(err)
			}
		}
		getJobs()
	}, [])

	useEffect(() => {
		if (dataExport && csvLink && csvLink.current && csvLink.current.link) {
			setTimeout(() => {
				csvLink.current.link.click()
				setDataExport([])
			})
		}
	}, [dataExport])

	const getAllApplications = async () => {
		try {
			// const res = await axios.get(process.env.REACT_APP_ROUTE_GET_APPLICATION as string + `?pageSize=${dataAggregate}&startDate=${filters.startDate}&endDate=${filters.endDate}`)
			// setDataExport(res.data.applications.map((row: { appliedOn: moment.MomentInput }) => ({
			// 	...row,
			// 	appliedOn: moment(row.appliedOn).format('DD MMM YYYY')
			// })))
			const input = {
				startDate: filters.startDate,
				endDate: filters.endDate,
			}
			const returnedData = await BulkDataService.returnWorkbook(input)

			if (returnedData !== null) {
				const filename = `E2I 2024 - (${filters.startDate=== '' ? 'Initial' : dayjs(filters.startDate).format(
					'DD/MMM/YYYY'
				)} - ${filters.endDate === '' ? 'Current' : dayjs(filters.endDate).format('DD/MMM/YYYY')}).xlsx`
				// Save on Client Side
				saveAs(
					new window.Blob([returnedData], {
						type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
					}),
					filename
				)
			}
		} catch (err) {
			console.log(err)
		}
	}

	const numbersOnly = (val: string) => {
		const numsCheck = new RegExp('^[0-9]{1,6}$')//6 digits only
		if (numsCheck.test(val) || val === '') {
			setFilterPage(val)
		}
		if (parseInt(val) > pageCount) {
			setFilterPage(pageCount.toString())
		}
	}

	const submitPageNumber = () => {
		window.location.href = `/applications?page=${filterPage}&startDate=${filters.startDate}&endDate=${filters.endDate}`
	}

	const getStatus = (status: string) => {
		switch (status) {
		case 'SUCCESSFUL':
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#EBFFF1', border: '1px solid #2BCB5C', color: '#2BCB5C' }}>{status}</span>

		case 'UNSUCCESSFUL':
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#FFF2F2', border: '1px solid #FE6565', color: '#FE6565' }}>{status}</span>

		case 'PENDING':
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#FFF3ED', border: '1px solid #FE9465', color: '#FE9465' }}>{status}</span>

		case 'VIEWED':
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#F3EFFF', border: '1px solid #8E6CFA', color: '#8E6CFA' }}>{status}</span>

		case 'SHORTLISTED':
			// return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#E5F5FF', border: '1px solid #1CA8FF', color: '#1CA8FF' }}>{status}</span>
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#EBFFF1', border: '1px solid #2BCB5C', color: '#2BCB5C' }}>CONTACTUNLOCKED</span>

		case 'CONTACTUNLOCKED':
			return <span className={`badge ${styles.customBadge}`} style={{ backgroundColor: '#EBFFF1', border: '1px solid #2BCB5C', color: '#2BCB5C' }}>{status}</span>
		}
	}

	const viewApplicationDetails = async (id: number) => {
		try {
			const res = await axios.get(process.env.REACT_APP_ROUTE_GET_EMPLOYER_ACTIONS as string + `/${id}`)
			
			setEmployerAction(res.data)
			setShow(true)
		} catch (err) {
			console.log(err)
		}
	}

	return (
		<div>
			<Row>
				<Col>
					<Filter />
				</Col>
			</Row>
			<Row className={styles.searchCompRow}>
				<Col style={{ display: 'flex', flexFlow: 'row wrap' }}>
					<div className={styles.dateCont}>
						<div className={styles.filterDateDiv}>
							<Image src='/img/cal.png' alt='search' width='15' height='15' />
							<DayPickerInput
								formatDate={(str) => dayjs(str).format('YYYY-MM-DD')}
								onDayChange={(day) => handleDateChange(dayjs(day).format('YYYY-MM-DD'), endDate)}
								overlayComponent={CustomOverlay}
								placeholder={'Start Date'}
								value={startDate}
							/>
						</div>
					</div>
					<div className={`d-none d-sm-block ${styles.dateArrow}`}>
						<FontAwesomeIcon icon={faArrowRight} size="xs" color="#9FB1C7" />
					</div>
					<div className={styles.dateCont}>
						<div className={styles.filterDateDiv}>
							<Image src='/img/cal.png' alt='search' width='15' height='15' />
							<DayPickerInput
								formatDate={(str) => dayjs(str).format('YYYY-MM-DD')}
								onDayChange={(day) => handleDateChange(startDate, dayjs(day).format('YYYY-MM-DD'))}
								overlayComponent={CustomOverlay}
								placeholder={'End Date'}
								value={endDate}
							/>
						</div>
					</div>
					<div className={styles.vertical__align}>
						<Button
							variant='link'
							onClick={() => {
								handleDateChange('', '')
							}}
						>
							Clear
						</Button>
					</div>
				</Col>
				<Col lg={2} md={2}>
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<div onClick={getAllApplications}>
							<EButton
								buttonText='Export'
								icon={<FontAwesomeIcon icon={faFileDownload} />} isSubmitting={false} className={styles.cusBtn}
							/>
						</div>
						{dataExport.length > 0 ?
							<CSVLink
								data={dataExport}
								headers={exportHeader}
								filename={'Jobs.csv'}
								ref={csvLink}
							/>
							: undefined
						}
					</div>
				</Col>
			</Row>
			{/* table */}
			{/* header */}
			<div style={{ overflowX: 'auto' }}>
				<table style={{ width: '100%' }}>
					<div className={styles.tableHeader}>
						<Row className={styles.mobileViewSlideTable}>
							{['Job position', 'Company name', 'Applicant name', 'Status', 'Applied on', 'Kiosk'].map((val, index) => (
								<Col key={index} xs={2} className={index !== 0 ? 'd-flex align-items-center justify-content-center' : ''}>
									<h6>{val}</h6>
								</Col>
							))}
						</Row>
					</div>
					{/* items */}
					<div>
						{isLoading ? (
							<div className='text-center mt-3'>
								<p>Loading</p>
							</div>
						) : (
							jobApplications && jobApplications.map((applicant, index) => {
								return (
									<div
										key={index}
										className={styles.tableBody}
										style={{ cursor: 'pointer' }}
										onClick={() => viewApplicationDetails(applicant.id)}
									>
										<Row className={styles.mobileViewSlideTable}>
											<Col xs={2}>
												<p style={{ textAlign: 'left', overflowWrap: 'break-word', paddingLeft: '1rem' }}>
													{applicant.jobApplied}
												</p>
											</Col>
											<Col xs={2} className={styles.flex}><p>{applicant.company}</p></Col>
											<Col xs={2} className={styles.flex}><p>{applicant.applicantName}</p></Col>
											<Col xs={2} className={styles.flex}>
												{getStatus(applicant.status)}
											</Col>
											<Col xs={2} className={`${styles.flex}`}><p>{moment(applicant.appliedOn).format('DD MMM YYYY')}</p></Col>
											<Col xs={2} className={styles.flex}>
												{applicant.kiosk}
											</Col>
										</Row>
									</div>
								)
							})
						)}
					</div>
				</table>
			</div>
			<Row style={{ marginTop: '20px' }}>
				<Col />
				<Col>
					<ReactPaginate
						containerClassName={styles.pagination}
						previousClassName="page-item"
						nextLinkClassName="page-link"
						// activeClassName="active"
						activeLinkClassName={styles.paginationActive}
						pageClassName="page-item"
						pageLinkClassName="page-link"
						previousLinkClassName="page-link"
						nextClassName="page-item"
						breakLinkClassName="page-link"
						breakClassName="page-item"
						previousLabel={<FontAwesomeIcon icon={faChevronLeft} />}
						pageCount={pageCount}
						onPageChange={handlePageClick}
						nextLabel={<FontAwesomeIcon icon={faChevronRight} />}
						pageRangeDisplayed={3}
						marginPagesDisplayed={3}
						breakLabel="..."
						forcePage={toPage}
					/>
				</Col>
				<Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<span style={{ fontSize: '14px', color: '#5B7493', paddingTop: '5px' }}>Go to page</span>
					<FormControl
						style={{ width: '15%', display: 'inline-block', margin: '0 10px', height: '35px' }}
						onChange={e => {
							numbersOnly(e.target.value)
						}}
						value={filterPage}
					/>
					<Button onClick={submitPageNumber} variant='primary' className={styles.cusBtn}>
						Go
					</Button>
				</Col>
			</Row>

			<Modal show={show} size='xl' onHide={() => setShow(false)}>
				<Modal.Header closeButton className='border-0' />
				<Modal.Body className='px-5 pt-0'>
					<div className="row text-center justify-content-center mb-5">
						<div className="col-xl-6 col-lg-8">
							<h3 className="font-weight-bold">Employer Actions</h3>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<div className="timeline-steps aos-init aos-animate" data-aos="fade-up">
								<div className="timeline-step inactive-step">
									<div className="timeline-content" data-toggle="popover" data-trigger="hover" data-placement="top" title="" data-content="And here's some amazing content. It's very engaging. Right?" data-original-title="2003">
										<div className='inner-circle'></div>
										<p className="h6 mt-3 mb-1">Pending</p>
										<p className="h6 text-muted mb-0 mb-lg-0">{pending !== null ? dayjs(pending).format('D MMM YYYY') : 'Pending'}</p>
									</div>
								</div>
								<div className="timeline-step">
									<div className="timeline-content" data-toggle="popover" data-trigger="hover" data-placement="top" title="" data-content="And here's some amazing content. It's very engaging. Right?" data-original-title="2003">
										<div className='inner-circle'></div>
										<p className="h6 mt-3 mb-1">Viewed</p>
										<p className="h6 text-muted mb-0 mb-lg-0">{viewed !== null ? dayjs(viewed).format('D MMM YYYY') : 'Pending'}</p>
									</div>
								</div>
								<div className="timeline-step">
									<div className="timeline-content" data-toggle="popover" data-trigger="hover" data-placement="top" title="" data-content="And here's some amazing content. It's very engaging. Right?" data-original-title="2004">
										<div className="inner-circle"></div>
										<p className="h6 mt-3 mb-1">Unlocked</p>
										<p className="h6 text-muted mb-0 mb-lg-0">{unlocked !== null ? dayjs(unlocked).format('D MMM YYYY') : 'Pending'}</p>
									</div>
								</div>
								{/* <div className="timeline-step">
									<div className="timeline-content" data-toggle="popover" data-trigger="hover" data-placement="top" title="" data-content="And here's some amazing content. It's very engaging. Right?" data-original-title="2005">
										<div className="inner-circle"></div>
										<p className="h6 mt-3 mb-1">Shortlisted</p>
										<p className="h6 text-muted mb-0 mb-lg-0">{shortlisted !== null ? dayjs(shortlisted).format('D MMM YYYY') : 'Pending'}</p>
									</div>
								</div> */}
								{/* <div className="timeline-step mb-0">
									<div className="timeline-content" data-toggle="popover" data-trigger="hover" data-placement="top" title="" data-content="And here's some amazing content. It's very engaging. Right?" data-original-title="2010">
										<div className="inner-circle"></div>
										<p className="h6 mt-3 mb-1">{finalDecision !== null ? finalDecision : 'Accepted / Rejected'}</p>
										<p className="h6 text-muted mb-0 mb-lg-0">{acceptedRejected !== null ? dayjs(acceptedRejected).format('D MMM YYYY') : 'Pending'}</p>
									</div>
								</div> */}
							</div>
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</div>
	)
}

export default JobApplications